import React from 'react';
import ReactApexChart from 'react-apexcharts';
import {
  format,
  parseISO,
  eachDayOfInterval,
  isSameDay,
  subDays,
  startOfDay
} from 'date-fns';
import { DATE_FORMAT_DISPLAY } from '../../../../constants';

import DurationDisplay from '../DurationDisplay';

const TrendGraph = ({ data = [], startDate, endDate, title, durationProps }) => {

  const allDatesInRange = React.useMemo(() => {
    const today = startOfDay(new Date());
    let finalStart;
    let finalEnd;

    if (startDate && !endDate) {
      finalStart = new Date(startDate);
      finalEnd = today;
    } else if (!startDate && endDate) {
      finalEnd = new Date(endDate);
      finalStart = subDays(finalEnd, 6);
    } else {
      finalStart = new Date(startDate);
      finalEnd = new Date(endDate);
    }

    return eachDayOfInterval({ start: finalStart, end: finalEnd });
  }, [startDate, endDate]);

  const categoriesData = allDatesInRange.map(date => format(date, DATE_FORMAT_DISPLAY));

  // Detect format and create series
  const series = React.useMemo(() => {
    if (!data.length) return [];

    // Check if data is single line format (array of {date, count})
    const isSingleLine = data[0]?.date !== undefined;

    if (isSingleLine) {
      // Filter out entries with null dates
      const validData = data.filter(item => item.date != null);
      return [{
        name: "Applications",
        data: allDatesInRange.map(date => {
          const existing = validData.find(item => isSameDay(parseISO(item.date), date));
          return existing ? existing.count : 0;
        })
      }];
    }

    // Multi-line format
    return data.map(serie => {
      // Filter out entries with null dates
      const validCounts = serie.count?.filter(item => item.date != null) || [];
      return {
        name: serie.job_name,
        data: allDatesInRange.map(date => {
          const existing = validCounts.find(item => isSameDay(parseISO(item.date), date));
          return existing ? existing.count : 0;
        })
      };
    });
  }, [data, allDatesInRange]);

  // Calculate optimal number of x-axis ticks based on date range
  const tickAmount = React.useMemo(() => {
    const totalDates = allDatesInRange.length;
    if (totalDates <= 15) return totalDates - 1; // Show all labels for small ranges
    if (totalDates <= 60) return 10; // ~weekly for 2 months
    if (totalDates <= 180) return 12; // ~bi-weekly for 6 months
    return 15; // ~monthly for larger ranges
  }, [allDatesInRange]);

  const chartOptions = {
    chart: {
      height: 350,
      type: 'line',
      zoom: { enabled: false },
      toolbar: {
        show: true,
        export: { png: { show: true }, csv: { show: false }, svg: { show: false } }
      }
    },
    stroke: { curve: 'smooth', width: 3 },
    colors: ['#242656', '#FF6B6B', '#4ECDC4', '#FFA500', '#6A5ACD'],
    xaxis: {
      categories: categoriesData,
      tickAmount: tickAmount,
      labels: {
        rotate: -45,
        rotateAlways: false,
        hideOverlappingLabels: true,
        style: { fontSize: '12px' }
      }
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: { formatter: val => Math.floor(val) }
    },
    tooltip: { x: { format: DATE_FORMAT_DISPLAY } }
  };

  return (
    <div className="app">
      <h1 className='heading-sm'>{title}</h1>
      <div id="chart">
        <ReactApexChart options={chartOptions} series={series} type="line" height={350} />
      </div>
      {durationProps && (
        <DurationDisplay
          dateType={durationProps.dateType}
          dateRangeLabel={durationProps.dateRangeLabel}
          dateRangeTooltip={durationProps.dateRangeTooltip}
          dateRangeDetail={durationProps.dateRangeDetail}
          monthsCount={durationProps.monthsCount}
        />
      )}
    </div>
  );
};

export default TrendGraph;